import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import DOMPurify from "dompurify";
import { useQueryClient } from "@tanstack/react-query";

interface TrackingCodes {
  head_scripts: string | null;
  body_scripts: string | null;
}

export const useTrackingCodes = () => {
  const [loaded, setLoaded] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    let idle: number | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const loadAndInjectTrackingCodes = async () => {
      try {
        // Use the safe public view that only exposes non-sensitive fields
        const data = await queryClient.fetchQuery({
          queryKey: ["public-tracking-settings"],
          queryFn: async () => {
            const { data, error } = await supabase.from("tracking_codes_public")
              .select("head_scripts, body_scripts, facebook_url").limit(1).maybeSingle();
            if (error) throw error;
            return data;
          },
          staleTime: 5 * 60 * 1000,
        });

        // Silently handle missing data or permission errors - this is expected for anonymous users
        if (cancelled || !data) {
          return;
        }

        const trackingData = data as unknown as TrackingCodes;

        // Inject head scripts
        if (trackingData?.head_scripts) {
          injectScripts(trackingData.head_scripts, "head");
        }

        // Inject body scripts
        if (trackingData?.body_scripts) {
          injectScripts(trackingData.body_scripts, "body");
        }

        setLoaded(true);
      } catch {
        // Silently fail - tracking codes are optional
      }
    };

    // Conversion routes stay eager; menu pages let the first paint happen first.
    const conversionRoute = window.location.pathname === "/confirm-subscription";
    const schedule = () => {
      if ("requestIdleCallback" in window) {
        idle = window.requestIdleCallback(() => { void loadAndInjectTrackingCodes(); }, { timeout: 500 });
      } else {
        timer = setTimeout(() => { void loadAndInjectTrackingCodes(); }, 0);
      }
    };
    if (conversionRoute) void loadAndInjectTrackingCodes();
    else if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener("load", schedule);
      if (idle !== undefined) window.cancelIdleCallback(idle);
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [queryClient]);

  return { loaded };
};

// Allowlist of known safe tracking domains
const SAFE_SCRIPT_DOMAINS = [
  "googletagmanager.com",
  "google-analytics.com",
  "www.google-analytics.com",
  "connect.facebook.net",
  "www.facebook.com",
  "static.hotjar.com",
  "script.hotjar.com",
  "clarity.ms",
  "www.clarity.ms",
];

const isScriptFromSafeDomain = (src: string): boolean => {
  try {
    const url = new URL(src);
    return SAFE_SCRIPT_DOMAINS.some(domain => 
      url.hostname === domain || url.hostname.endsWith("." + domain)
    );
  } catch {
    return false;
  }
};

const injectScripts = (htmlContent: string, target: "head" | "body") => {
  // First, sanitize with DOMPurify to remove dangerous attributes
  // Allow script tags but DOMPurify will strip dangerous event handlers
  const sanitizedHtml = DOMPurify.sanitize(htmlContent, {
    ADD_TAGS: ["script", "noscript", "iframe"],
    ADD_ATTR: ["src", "async", "defer", "id", "class", "data-*"],
    FORCE_BODY: true,
  });

  const container = document.createElement("div");
  container.innerHTML = sanitizedHtml;

  const targetElement = target === "head" ? document.head : document.body;

  // Process all child nodes
  Array.from(container.childNodes).forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      
      if (element.tagName === "SCRIPT") {
        const src = element.getAttribute("src");
        
        // For external scripts, only allow from known safe domains
        if (src && !isScriptFromSafeDomain(src)) {
          console.warn(`Blocked script from untrusted domain: ${src}`);
          return;
        }
        
        // Create a new script element to ensure it executes
        const script = document.createElement("script");
        
        // Copy only safe attributes
        const safeAttrs = ["src", "async", "defer", "id", "class", "type"];
        Array.from(element.attributes).forEach((attr) => {
          if (safeAttrs.includes(attr.name) || attr.name.startsWith("data-")) {
            script.setAttribute(attr.name, attr.value);
          }
        });
        
        // Copy inline script content (already sanitized)
        if (element.textContent) {
          script.textContent = element.textContent;
        }
        
        if (src && Array.from(document.scripts).some(existing => existing.src === script.src)) return;
        targetElement.appendChild(script);
      } else if (element.tagName === "NOSCRIPT") {
        // Handle noscript tags
        const noscript = document.createElement("noscript");
        noscript.innerHTML = DOMPurify.sanitize(element.innerHTML);
        targetElement.appendChild(noscript);
      } else {
        // Clone other elements with sanitization
        const sanitizedElement = DOMPurify.sanitize(element.outerHTML, {
          ADD_TAGS: ["iframe"],
          ADD_ATTR: ["src", "frameborder", "allowfullscreen", "loading"],
        });
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = sanitizedElement;
        if (tempDiv.firstChild) {
          targetElement.appendChild(tempDiv.firstChild);
        }
      }
    } else if (node.nodeType === Node.COMMENT_NODE) {
      // Preserve comments (like GTM comments)
      targetElement.appendChild(node.cloneNode(true));
    }
  });
};
